// ocs-proto's decrypt/encrypt/AAD entry points take an event (or header) as CBOR, while
// `decodeFrame` hands events over as JavaScript objects. Rather than write a second CBOR
// encoder here (docs/09 principle 4), these helpers run the object through the crate's
// own frame encoder and cut the event out of the `event` frame around it:
//
//   A3 "type" "event" "event" <event> "meta" ""     (ciborium, fields in declaration order)
//
// The fixed bytes are asserted on every call so a layout change in the crate fails loudly
// here instead of producing garbage; vectors/events.json and aad.json pin the result.
import type { Proto } from "./index";
import { bytesEqual, concat, hexToBytes } from "./bytes";
import type { EncryptedEvent, EventHeader } from "./frames";

const FRAME_PREFIX = hexToBytes("a3 64 74797065 65 6576656e74 65 6576656e74");
const FRAME_SUFFIX = hexToBytes("64 6d657461 60");

// Inside the event map, the header is the first field; with an empty ciphertext what
// follows it is fixed: "nonce" (12 bytes), "ciphertext" (empty), "tag" (16 bytes).
const HEADER_PREFIX = hexToBytes("a4 66 686561646572");
const HEADER_SUFFIX_LEN = 1 + 5 + 1 + 12 + (1 + 10 + 1) + (1 + 3 + 1 + 16);
const EMPTY_NONCE = new Uint8Array(12);
const EMPTY_TAG = new Uint8Array(16);

export class ProtoLayoutError extends Error {
  constructor(what: string) {
    super(`ocs-proto frame layout changed (${what}); update proto/event-cbor.ts`);
    this.name = "ProtoLayoutError";
  }
}

function cut(bytes: Uint8Array, prefix: Uint8Array, suffixLen: number, suffix: Uint8Array | undefined, what: string): Uint8Array {
  const end = bytes.length - suffixLen;
  if (end <= prefix.length || !bytesEqual(bytes.subarray(0, prefix.length), prefix)) throw new ProtoLayoutError(what);
  if (suffix && !bytesEqual(bytes.subarray(end), suffix)) throw new ProtoLayoutError(what);
  return bytes.slice(prefix.length, end);
}

/** The CBOR of one `EncryptedEvent`, as `decryptEvent` expects it. */
export function eventToCbor(proto: Proto, event: EncryptedEvent): Uint8Array {
  const frame = proto.encodeFrame({ type: "event", event, meta: "" });
  return cut(frame, FRAME_PREFIX, FRAME_SUFFIX.length, FRAME_SUFFIX, "event frame");
}

/** The inverse: an `EncryptedEvent` object from its CBOR (what `encryptInboundEvent` returns). */
export function eventFromCbor(proto: Proto, cbor: Uint8Array): EncryptedEvent {
  const frame = proto.decodeFrame(concat(FRAME_PREFIX, cbor, FRAME_SUFFIX));
  if (frame.type !== "event") throw new ProtoLayoutError("event frame type");
  return frame.event;
}

/** The CBOR of one `EventHeader`, as `eventAad` and `encryptInboundEvent` expect it. */
export function headerToCbor(proto: Proto, header: EventHeader): Uint8Array {
  const event = eventToCbor(proto, { header, nonce: EMPTY_NONCE, ciphertext: new Uint8Array(0), tag: EMPTY_TAG });
  return cut(event, HEADER_PREFIX, HEADER_SUFFIX_LEN, undefined, "event header");
}
